import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton, QESCheckbox, QESTextInput } from "@app/shared/qes-standards";
import { useState, JSX, useContext, useEffect, useRef } from "react"
import { NetworkProviderCard } from "../NetworkProviderCard";
import { ImpactProviderCard } from "../ImpactProviderCard";
import { FilterContext } from "../contexts/FilterContext";
import { NetworkCoverageMapDataContext } from "../contexts/NetworkCoverageMapDataContext";
import { ImpactProviderMapResults, NetworkProviderMapResults } from "@app/projects/results/common/mapping";
import { LoadingSpinner, TooltipWrapper } from "@app/shared";
import { ProvidersSortOptions, ImpactProvidersFilterOptions, ProviderAccordionSelectedTab, ProviderSourceOfSelection } from "../enumerators";
import { PROVIDER_ACCORDION_BUTTON_COLOR } from "../NetworkCoverageMapsColors";
import { ProjectionProviderType, ProviderNetworkType } from "@app/projects/enumerators";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";
import { createPortal } from "react-dom";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";

interface ProviderAccordionControlProps extends MapCustomControlProps{
  updateMapInputs: () => void;
}

interface ImpactProvidersFilterOptionsType {
    id: string;
    name: string;
    type: ImpactProvidersFilterOptions;
    children?: {
        id: string;
        name: string;
        type: ImpactProvidersFilterOptions;
    }[];
}

export const ProviderAccordionControl = ({ updateMapInputs, ignoreInMapExport, mapRef, position}: ProviderAccordionControlProps): JSX.Element => {

  const networkCoverageMapDataContext = useContext(NetworkCoverageMapDataContext);
  const filterContext = useContext(FilterContext);

  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [filterIconStyle, setFilterIconStyle] = useState<string>('2px solid transparent');

  const [showNetworkProvidersTab, setShowNetworkProvidersTab] = useState(true);
  const [showOutOfNetworkProvidersTab, setShowOutOfNetworkProvidersTab] = useState(false);
  const [hideMarketFilter, setHideMarketFilter] = useState(false);
  const [hideProspectFilter, setHideProspectFilter] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const providersSortOptions = [
    {
      id: 'provider-accordion-sort-alphabetical',
      name: 'Name (A-Z)',
      type: ProvidersSortOptions.Alphabetical,
    },
    {
      id: 'provider-accordion-sort-reverse-alphabetical',
      name: 'Name (Z-A)',
      type: ProvidersSortOptions.ReverseAlphabetical,
    },
    {
      id: 'provider-accordion-sort-serves-high-to-low',
      name: 'Serves % (High to Low)',
      type: ProvidersSortOptions.ServesHighToLow,
      visibility: ProviderAccordionSelectedTab.NetworkProviders
    },
    {
      id: 'provider-accordion-sort-serves-low-to-high',
      name: 'Serves % (Low to High)',
      type: ProvidersSortOptions.ServesLowToHigh,
      visibility: ProviderAccordionSelectedTab.NetworkProviders
    },
    {
      id: 'provider-accordion-sort-gains-high-to-low',
      name: 'Gains % (High to Low)',
      type: ProvidersSortOptions.GainsHighToLow,
      visibility: ProviderAccordionSelectedTab.OutOfNetworkProviders
    },
    {
      id: 'provider-accordion-sort-gains-low-to-high',
      name: 'Gains % (Low to High)',
      type: ProvidersSortOptions.GainsLowToHigh,
      visibility: ProviderAccordionSelectedTab.OutOfNetworkProviders
    },
    {
      id: 'provider-accordion-sort-impacts-high-to-low',
      name: 'Impacts % (High to Low)',
      type: ProvidersSortOptions.ImpactsHighToLow,
      visibility: ProviderAccordionSelectedTab.OutOfNetworkProviders
    },
    {
      id: 'provider-accordion-sort-impacts-low-to-high',
      name: 'Impacts % (Low to High)',
      type: ProvidersSortOptions.ImpactsLowToHigh,
      visibility: ProviderAccordionSelectedTab.OutOfNetworkProviders
    }
  ];

  const baseImpactProvidersFilterOptions: ImpactProvidersFilterOptionsType[] = [
    {
      id: 'provider-accordion-filter-market',
      name: 'Market Providers',
      type: ImpactProvidersFilterOptions.Market,
    },
    {
      id: 'provider-accordion-filter-prospect',
      name: 'Prospect Providers',
      type: ImpactProvidersFilterOptions.Prospect,
    },
  ];

  const [impactProvidersFilterOptions, setImpactProviderFilterOptions] = useState<ImpactProvidersFilterOptionsType[]>(baseImpactProvidersFilterOptions);

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen);
  }

  const providerCardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const providerCardAccordionRef = useRef<HTMLDivElement | null>(null);

  const setProviderCardRef = (id: number, node: HTMLDivElement | null) => {
    providerCardRefs.current[id] = node;
  };

  useEffect(() => {
    if (filterContext.selectedProviderSources.includes(ProviderNetworkType.Network)) {
      setShowNetworkProvidersTab(true);
      networkCoverageMapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.NetworkProviders);
    }
    else {
      setShowNetworkProvidersTab(false);
    }

    if (filterContext.selectedProviderSources.includes(ProviderNetworkType.Market) ||
      filterContext.selectedProviderSources.includes(ProviderNetworkType.Prospect)) {
      setShowOutOfNetworkProvidersTab(true);

      if (!filterContext.selectedProviderSources.includes(ProviderNetworkType.Network)) {
        networkCoverageMapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.OutOfNetworkProviders);
      }
    }
    else {
      setShowOutOfNetworkProvidersTab(false);
    }

    setHideProspectFilter(!filterContext.selectedProviderSources.includes(ProviderNetworkType.Prospect));
    setHideMarketFilter(!filterContext.selectedProviderSources.includes(ProviderNetworkType.Market));
  }, [filterContext.selectedProviderSources]);

  useEffect(() => {
    // check if there are any target providers in the impact providers
    if (networkCoverageMapDataContext.impactProviders && networkCoverageMapDataContext.impactProviders.some(p => p.projectionType === ProjectionProviderType.Target)) {
      const targetFilterOptions: ImpactProvidersFilterOptionsType[] = [];

      if (networkCoverageMapDataContext.impactProviders.some(p => p.providerNetworkType === ProviderNetworkType.Market && p.projectionType === ProjectionProviderType.Target)) {
        targetFilterOptions.push({
          id: 'provider-accordion-filter-targeted-market',
          name: 'Targeted Market Providers',
          type: ImpactProvidersFilterOptions.TargetedMarket,
        });
      }

      if (networkCoverageMapDataContext.impactProviders.some(p => p.providerNetworkType === ProviderNetworkType.Prospect && p.projectionType === ProjectionProviderType.Target)) {
        targetFilterOptions.push({
          id: 'provider-accordion-filter-targeted-prospect',
          name: 'Targeted Prospect Providers',
          type: ImpactProvidersFilterOptions.TargetedProspect,
        });
      }

      if (targetFilterOptions.length > 0) {
        setImpactProviderFilterOptions([...baseImpactProvidersFilterOptions, {
          children: targetFilterOptions,
          id: 'provider-accordion-filter-targeted',
          name: 'Targeted Providers',
          type: ImpactProvidersFilterOptions.Target,
        }]);

        // During the first page load, where the dataRefreshCounter is zero, set the selected filter option as needed. Otherwise, keep the currently selected filters
        if (networkCoverageMapDataContext.dataRefreshCounter == 0) { 
          networkCoverageMapDataContext.setSelectedProviderFilterOptions((x) => [...x, ...targetFilterOptions.map((o) => o.type).filter((o) => !networkCoverageMapDataContext.selectedProviderFilterOptions.includes(o))]);
        }
        
      }
    }
  }, [networkCoverageMapDataContext.impactProviders, networkCoverageMapDataContext.dataRefreshCounter]);

  const handleProviderCardClick = (provider: NetworkProviderMapResults | ImpactProviderMapResults) => {
    networkCoverageMapDataContext.setSelectedProvider(provider);
    networkCoverageMapDataContext.setSelectedProviderSourceOfSelection(ProviderSourceOfSelection.Accordion);
    networkCoverageMapDataContext.setIsProviderPopupOpen(true);
  };

  useEffect(() => {

    // Switch to the Out-of-Network tab if one Out-of-Network provider is clicked and the current active tab is Network
    if (networkCoverageMapDataContext.providerAccordionSelectedTab == ProviderAccordionSelectedTab.NetworkProviders &&
      networkCoverageMapDataContext.impactProviders?.some((p) => p.providerId === networkCoverageMapDataContext.selectedProvider?.providerId) &&
      networkCoverageMapDataContext.selectedProviderSourceOfSelection == ProviderSourceOfSelection.Map) {
      networkCoverageMapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.OutOfNetworkProviders);
    }

    // Switch to the Network tab if one Network provider is clicked and the current active tab is Out-of-Network
    if (networkCoverageMapDataContext.providerAccordionSelectedTab == ProviderAccordionSelectedTab.OutOfNetworkProviders &&
      networkCoverageMapDataContext.networkProviders?.some((p) => p.providerId === networkCoverageMapDataContext.selectedProvider?.providerId) &&
      networkCoverageMapDataContext.selectedProviderSourceOfSelection == ProviderSourceOfSelection.Map) {
      networkCoverageMapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.NetworkProviders);
    }

    const scrollToProviderCard = () => {
      const accordionContainer = providerCardAccordionRef.current;
      const providerCard = providerCardRefs.current[networkCoverageMapDataContext.selectedProvider?.providerId];

      if (!accordionContainer || !providerCard) return;

      const accordionContainerHeight = accordionContainer.clientHeight;

      const providerCardOffsetTop = providerCard.offsetTop;
      const providerCardHeight = providerCard.offsetHeight;

      const scrollTo = providerCardOffsetTop - (accordionContainerHeight / 2) + (providerCardHeight / 2);

      accordionContainer.scrollTo({
        behavior: 'smooth',
        top: scrollTo
      });
    };

    //Add this timeout to make sure the DOM gets updated after having changed tabs. This will account for also being able to automatically scroll to the provider card when the corresponding icon was clicked from the other tab.
    const timeout = setTimeout(scrollToProviderCard, 50);

    return () => clearTimeout(timeout);

  }, [networkCoverageMapDataContext.selectedProvider, networkCoverageMapDataContext.selectedProviderSourceOfSelection, networkCoverageMapDataContext.networkProviders, networkCoverageMapDataContext.impactProviders, networkCoverageMapDataContext.providerAccordionSelectedTab]);

  const container = useMapboxControl({mapRef, position});
  if (!container) return null;

  return createPortal (
    <div className="network-coverage-map__provider-accordion" {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      {isAccordionOpen &&
        <div
          className="network-coverage-map__provider-accordion-container"
          style={{
            height: mapRef.current?.getCanvas().getBoundingClientRect().height
          }}
        >
          <div ref={headerRef}>
            <div className="tabs-style-2 network-coverage-map__provider-accordion-tabs">
              <ul className="nav nav-tabs nav-fill" role="menu">
                {showNetworkProvidersTab &&
                  <li className={`nav-item ${!showOutOfNetworkProvidersTab ? 'w-100 text-center' : ''}`} role="none">
                    <a
                      className={`nav-link ${networkCoverageMapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.NetworkProviders ? 'active' : ''}`}
                      onClick={() => networkCoverageMapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.NetworkProviders)}
                      role="menuitem"
                    >
                      Network Providers
                    </a>
                  </li>
                }
                {showOutOfNetworkProvidersTab &&
                  <li className={`nav-item ${!showNetworkProvidersTab ? 'w-100 text-center' : ''}`} role="none">
                    <a
                      className={`nav-link ${networkCoverageMapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.OutOfNetworkProviders ? 'active' : ''}`}
                      onClick={() => networkCoverageMapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.OutOfNetworkProviders)}
                      role="menuitem"
                    >
                      <span>
                        Out-Of-Network Providers
                        <TooltipWrapper
                          text="Provider options that service this county but are currently not in your network"
                          position="right"
                          containerClassName="ml-2"
                          tooltipWidth="20rem"
                        >
                          <QesIcon
                            iconName={QesIcons.FontAwesome.InfoCircle}
                            className="network-coverage-map__provider-accordion-tooltip-icon"
                            color="#828282"
                            height="16"
                            width="16"
                          />
                        </TooltipWrapper>
                      </span>
                    </a>
                  </li>
                }
              </ul>
            </div>
            <div className="network-coverage-map__provider-accordion-header">
              <QESTextInput
                label={'Search'}
                hideLabel
                placeholder="Search by name or NPI"
                testId="network-coverage-map__provider-accordion-search"
                value={networkCoverageMapDataContext.providerSearchText}
                onChange={(e) => networkCoverageMapDataContext.setProviderSearchText(e.target.value)}
                inputAppend={<QesIcon iconName={QesIcons.FontAwesome.Search} />}
              />
              <div>
                <NewActionMenu
                  buttonClassNames="qes-button-secondary"
                  containerClassnames="network-coverage-map__provider-accordion-sort"
                  buttonText={
                    <>
                      <QesIcon
                        iconName={QesIcons.Untitled.SwitchVerticalV1}
                      />&nbsp;
                      <span>Sort by</span>
                    </>
                  }
                  testId="network-coverage-map__provider-accordion-sort"
                  hideArrow
                >
                  {
                    providersSortOptions.filter(r => r.visibility == null || r.visibility === networkCoverageMapDataContext.providerAccordionSelectedTab).map((option) => (
                      <NewActionMenuItem
                        display={option.name}
                        key={option.type}
                        onClick={() => {
                          networkCoverageMapDataContext.setSelectedProviderSortOption(option.type);
                        }}
                        isSelected={networkCoverageMapDataContext.selectedProviderSortOption === option.type}
                        testId={option.id}
                      />
                    ))
                  }
                </NewActionMenu>
                {
                  networkCoverageMapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.OutOfNetworkProviders &&
                  <NewActionMenu
                    buttonClassNames="qes-button-secondary"
                    containerClassnames="network-coverage-map__provider-accordion-filter"
                    buttonText={
                      <>
                        <QesIcon
                          iconName={QesIcons.Untitled.FilterLines}
                        />&nbsp;
                        <span>Filter by</span>
                      </>
                    }
                    testId="network-coverage-map__provider-accordion-filter"
                    hideArrow
                  >
                    {
                      impactProvidersFilterOptions.map((option) => {
                        const isHidden = (option.type === ImpactProvidersFilterOptions.Market && hideMarketFilter) ||
                          (option.type === ImpactProvidersFilterOptions.Prospect && hideProspectFilter);

                        return !isHidden ? (
                          <div key={option.type}>
                            <QESCheckbox
                              label={option.name}
                              onChange={() => {
                                if (!!option.children) {
                                  // if all the children are selected, unselect the children
                                  if (networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length == option.children?.length) {
                                    const selectedChildren = option.children.map((child) => child.type);
                                    networkCoverageMapDataContext.setSelectedProviderFilterOptions(networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => !selectedChildren.includes(o)));
                                  }
                                  // if not all the children are selected, select the children
                                  else {
                                    // filter out the children that are already selected (if any) to avoid duplicates
                                    const selectedChildren = option.children
                                      .map((child) => child.type)
                                      .filter((childType) => !networkCoverageMapDataContext.selectedProviderFilterOptions.includes(childType));
                                    networkCoverageMapDataContext.setSelectedProviderFilterOptions([...(networkCoverageMapDataContext.selectedProviderFilterOptions || []), ...selectedChildren]);
                                  }
                                  return;
                                }

                                if (networkCoverageMapDataContext.selectedProviderFilterOptions.includes(option.type)) {
                                  networkCoverageMapDataContext.setSelectedProviderFilterOptions(networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => o !== option.type));
                                }
                                else {
                                  networkCoverageMapDataContext.setSelectedProviderFilterOptions([...(networkCoverageMapDataContext.selectedProviderFilterOptions || []), option.type]);
                                }
                              }}
                              checked={networkCoverageMapDataContext.selectedProviderFilterOptions.includes(option.type) || !!option.children && option.children.length > 0
                                && networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length == option.children?.length}
                              indeterminate={!!option.children && option.children.length > 0
                                && networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length > 0
                                && networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => option.children?.some((child) => child.type === o)).length < option.children?.length}
                              testId={option.id}
                              containerClassName="network-coverage-map__provider-accordion-filter-checkbox action-menu-item col-12"
                            />

                            {!!option.children && option.children.length > 0 &&
                              <div className="ml-3">
                                {option.children.map((child) => (
                                  <QESCheckbox
                                    key={child.type}
                                    label={child.name}
                                    onChange={() => {
                                      if (networkCoverageMapDataContext.selectedProviderFilterOptions.includes(child.type)) {
                                        networkCoverageMapDataContext.setSelectedProviderFilterOptions(networkCoverageMapDataContext.selectedProviderFilterOptions.filter((o) => o !== child.type));
                                      }
                                      else {
                                        networkCoverageMapDataContext.setSelectedProviderFilterOptions([...(networkCoverageMapDataContext.selectedProviderFilterOptions || []), child.type]);
                                      }
                                    }}
                                    checked={networkCoverageMapDataContext.selectedProviderFilterOptions.includes(child.type)}
                                    testId={child.id}
                                    containerClassName="network-coverage-map__provider-accordion-filter-checkbox action-menu-item col-12"
                                  />
                                ))}
                              </div>}
                          </div>
                        ) : null;
                      })
                    }
                  </NewActionMenu>
                }
              </div>
            </div>
          </div>
          <div className="network-coverage-map__provider-accordion-content" >
            <div style={{
              height: `calc(${mapRef.current?.getCanvas().getBoundingClientRect().height}px - ${headerRef.current?.getBoundingClientRect().height + 25}px)`,
              overflow: 'auto',
            }}
              ref={providerCardAccordionRef}
            >
              {
                networkCoverageMapDataContext.loadingProviders
                  ? <div className="h-100 d-flex justify-content-center align-items-center">
                    <LoadingSpinner />
                  </div>
                  : networkCoverageMapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.OutOfNetworkProviders && networkCoverageMapDataContext.visibleImpactProviders && networkCoverageMapDataContext.visibleImpactProviders.length > 0
                    ? networkCoverageMapDataContext.visibleImpactProviders.map((p) =>
                      <div key={crypto.randomUUID()} ref={node => setProviderCardRef(p.providerId, node)}>
                        <ImpactProviderCard
                          isSelected={!!networkCoverageMapDataContext.selectedProvider && networkCoverageMapDataContext.selectedProvider?.providerId && p.providerId == networkCoverageMapDataContext.selectedProvider.providerId}
                          passPercentage={networkCoverageMapDataContext.passPercentage}
                          provider={p}
                          onClick={handleProviderCardClick} />
                      </div>)
                    : networkCoverageMapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.NetworkProviders && networkCoverageMapDataContext.visibleNetworkProviders && networkCoverageMapDataContext.visibleNetworkProviders.length > 0
                      ? networkCoverageMapDataContext.visibleNetworkProviders.map((p) =>
                        <div key={crypto.randomUUID()} ref={node => setProviderCardRef(p.providerId, node)}>
                          <NetworkProviderCard
                            isSelected={!!networkCoverageMapDataContext.selectedProvider && networkCoverageMapDataContext.selectedProvider?.providerId && p.providerId == networkCoverageMapDataContext.selectedProvider.providerId}
                            provider={p}
                            onClick={handleProviderCardClick} />
                        </div>)
                      : <p>{networkCoverageMapDataContext.providerFetchMessage}</p>
              }
            </div>
          </div>
        </div>
      }
      <QESButton
        id="network-coverage-map__provider-accordion-button"
        className="network-coverage-map__provider-accordion-button"
        onMouseOut={() => {
          setFilterIconStyle("2px solid transparent");
        }}
        onMouseOver={() => {
          setFilterIconStyle("2px solid black");
        }}
        onClick={toggleAccordion}
        qesType="secondary"
        qesTestId="network-coverage-map__provider-accordion-button"
        style={{
          backgroundColor: "white",
          border: filterIconStyle,
          color: '#828282',
          textDecoration: 'none'
        }}
      >
        {!isAccordionOpen && <span style={{ marginRight: '4px' }}>Show Providers</span>}
        <QesIcon
          iconName={QesIcons.FontAwesome.ChevronRight}
          className="network-coverage-map__provider-accordion-icon"
          color={PROVIDER_ACCORDION_BUTTON_COLOR}
          height="16"
          svgStyle={{
            backgroundColor: 'transparent',
            padding: '0px',
            transform: isAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          testId="network-coverage-map__filter-icon"
          width="16"
        />
      </QESButton>
      <QESButton
        qesTestId="network-coverage-map__provider-accordion-update-inputs-button"
        className="network-coverage-map__provider-accordion-update-inputs-button"
        qesType="none"
        onClick={updateMapInputs}
      >
        Update Inputs
      </QESButton>
    </div>,
    container
    )

}